import { useState } from 'react';
import { User } from '@/types';
import { offerLetterApi } from '@/lib/api';
import { downloadBlob } from '@/lib/utils';
import { getResponsibilityTemplate } from '@/lib/joiningLetterTemplates';
import { Button } from '@/components/ui/Button';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface JoiningLetterModalProps {
  employee: User;
  onClose: () => void;
}

type SubmitAction = 'download' | 'send';

function toInputDate(value: Date | string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function JoiningLetterModal({ employee, onClose }: JoiningLetterModalProps) {
  const template = getResponsibilityTemplate(employee.position);

  const [intro, setIntro] = useState(template.intro);
  const [bulletsText, setBulletsText] = useState(template.bullets.join('\n'));
  const [note, setNote] = useState(template.note);
  const [sectionTitle, setSectionTitle] = useState(template.sectionTitle);
  const [sectionBulletsText, setSectionBulletsText] = useState(
    template.sectionBullets.join('\n'),
  );
  const [reportingTo, setReportingTo] = useState('NowThingsToDo Management');
  const [probationPeriod, setProbationPeriod] = useState('3 months');
  const [joiningDate, setJoiningDate] = useState(toInputDate(employee.joiningDate));
  const [emailTo, setEmailTo] = useState(employee.email || '');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [loading, setLoading] = useState<SubmitAction | null>(null);

  const buildPayload = () => {
    const bullets = bulletsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const sectionBullets = sectionBulletsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    return {
      responsibilities: {
        intro: intro.trim(),
        bullets,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(sectionTitle.trim() && sectionBullets.length > 0
          ? {
              sections: [
                {
                  title: sectionTitle.trim(),
                  bullets: sectionBullets,
                },
              ],
            }
          : {}),
      },
      reportingTo: reportingTo.trim() || undefined,
      probationPeriod: probationPeriod.trim() || undefined,
      ...(joiningDate ? { joiningDate } : {}),
    };
  };

  const validateForm = (): boolean => {
    const bullets = bulletsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (!intro.trim()) {
      alert('Responsibilities intro is required.');
      return false;
    }

    if (bullets.length === 0) {
      alert('At least one responsibility bullet is required.');
      return false;
    }

    const sectionBullets = sectionBulletsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (sectionTitle.trim() && sectionBullets.length === 0) {
      alert('Section bullets are required when a section title is provided.');
      return false;
    }

    return true;
  };

  const handleDownload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading('download');
    try {
      const blob = await offerLetterApi.generateJoiningLetter(
        employee._id,
        buildPayload(),
      );

      downloadBlob(
        blob,
        `joining-letter-${employee.firstName}-${employee.lastName}.pdf`,
      );
      onClose();
    } catch (error: unknown) {
      console.error('Joining letter generation failed:', error);
      const axiosError = error as { response?: { data?: Blob } };
      if (axiosError.response?.data instanceof Blob) {
        try {
          const text = await axiosError.response.data.text();
          const parsed = JSON.parse(text);
          alert(parsed.error || 'Failed to generate joining letter.');
          return;
        } catch {
          // fall through
        }
      }
      alert('Failed to generate joining letter. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleSendEmail = async () => {
    if (!validateForm()) return;

    if (!emailTo.trim()) {
      alert('Recipient email is required.');
      return;
    }

    setLoading('send');
    try {
      const response = await offerLetterApi.sendJoiningLetter(employee._id, {
        ...buildPayload(),
        to: emailTo.trim(),
        ...(emailSubject.trim() ? { subject: emailSubject.trim() } : {}),
        ...(emailMessage.trim() ? { message: emailMessage.trim() } : {}),
      });

      if (!response.success) {
        alert(response.error || 'Failed to send joining letter.');
        return;
      }

      alert(
        response.message ||
          `Joining letter sent to ${response.data?.to || emailTo.trim()}.`,
      );
      onClose();
    } catch (error: unknown) {
      console.error('Joining letter email failed:', error);
      const axiosError = error as { response?: { data?: { error?: string } } };
      alert(
        axiosError.response?.data?.error ||
          'Failed to send joining letter. Please try again.',
      );
    } finally {
      setLoading(null);
    }
  };

  const isBusy = loading !== null;

  return (
    <div className="fixed inset-0 backdrop-blur-md bg-black/20 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-0">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Generate Joining Letter</h2>
              <p className="text-sm text-gray-600 mt-1">
                {employee.firstName} {employee.lastName} · {employee.position}
              </p>
            </div>
            <Button variant="outline" onClick={onClose} className="rounded-full hover:bg-gray-100">
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleDownload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsibilities Intro
              </label>
              <textarea
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsibility Bullets <span className="text-gray-500 font-normal">(one per line)</span>
              </label>
              <textarea
                value={bulletsText}
                onChange={(e) => setBulletsText(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Additional Section</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Title
                </label>
                <input
                  type="text"
                  value={sectionTitle}
                  onChange={(e) => setSectionTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Bullets <span className="text-gray-500 font-normal">(one per line)</span>
                </label>
                <textarea
                  value={sectionBulletsText}
                  onChange={(e) => setSectionBulletsText(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joining Date</label>
                <input
                  type="date"
                  value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reporting To</label>
                <input
                  type="text"
                  value={reportingTo}
                  onChange={(e) => setReportingTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Probation Period</label>
                <input
                  type="text"
                  value={probationPeriod}
                  onChange={(e) => setProbationPeriod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Email (optional)</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send To
                </label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder={employee.email || 'employee@example.com'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Your Joining Letter - Nowthingstodo Worldwide Ltd"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={3}
                  placeholder="Leave blank to use the default email message."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={onClose} disabled={isBusy}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSendEmail}
                disabled={isBusy}
              >
                {loading === 'send' ? 'Sending...' : 'Send Email'}
              </Button>
              <Button type="submit" disabled={isBusy} className="bg-blue-600 hover:bg-blue-700">
                {loading === 'download' ? 'Generating...' : 'Download PDF'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
